import React from 'react';
import {
  buildPagePlan,
  measurePrintBlocks,
  measureRenderedPrintBlocks,
  PRINT_ROW_ATTR,
  PRINT_SECTION_ATTR,
  PRINT_THEAD_ATTR,
  resolvePrintTemplate,
  type MeasuredBlocks,
  type PagePlan,
  type PlannedPage,
  type PlannedRowSegment,
  type PrintDocumentModel,
  type PrintRow,
  type RichPrintRenderPayload,
} from '@/print-engine';
import { PrintableApprovalSigners } from '@/components/approval/PrintableApprovalSigners';
import { PaginatedPrintShell } from './PaginatedPrintShell';

function sectionAttr(id: string): Record<string, string> {
  return { [PRINT_SECTION_ATTR]: id };
}

function pageHas(page: PlannedPage, id: string): boolean {
  return page.sectionIds.includes(id) || page.sections.includes(id);
}

function RowDescription({
  row,
  segment,
}: {
  row: PrintRow;
  segment: PlannedRowSegment;
}) {
  const chunks = row.descriptionChunks.slice(segment.chunkStart, segment.chunkEnd);
  return (
    <div>
      {segment.isContinuation ? (
        <div className="print-row-continued">Item {row.lineNumber} — Continued</div>
      ) : null}
      {chunks.map((c, i) => (
        <div key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: 2 }}>
          {c}
        </div>
      ))}
      {segment.showCommercial &&
        (row.extraLines || []).map((line) => (
          <div key={line} style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>
            {line}
          </div>
        ))}
    </div>
  );
}

function GenericTable({
  model,
  page,
}: {
  model: PrintDocumentModel;
  page: PlannedPage;
}) {
  const byTable = new Map<number, PlannedRowSegment[]>();
  for (const seg of page.itemRows) {
    const list = byTable.get(seg.tableIndex) || [];
    list.push(seg);
    byTable.set(seg.tableIndex, list);
  }

  return (
    <>
      {[...byTable.entries()].map(([tableIndex, segs]) => {
        const table = model.tables[tableIndex];
        if (!table) return null;
        const showHeader = page.tableHeaderIndexes.includes(tableIndex);
        return (
          <div key={table.id}>
            {table.title ? <h3 style={{ fontSize: 13, margin: '8px 0 6px' }}>{table.title}</h3> : null}
            <table className="print-generic-table">
              {showHeader ? (
                <thead {...{ [PRINT_THEAD_ATTR]: '' }}>
                  <tr>
                    {table.columns.map((col) => (
                      <th
                        key={col.key}
                        className={col.align === 'right' ? 'num' : col.key === 'sno' ? 'sno' : undefined}
                        style={{ width: col.width }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {segs.map((seg) => {
                  const row = table.rows[seg.itemIndex];
                  if (!row) return null;
                  return (
                    <tr
                      key={`${seg.tableIndex}-${seg.itemIndex}-${seg.chunkStart}`}
                      {...{ [PRINT_ROW_ATTR]: `${seg.tableIndex}:${seg.itemIndex}` }}
                    >
                      {table.columns.map((col) => {
                        if (col.key === 'sno') {
                          return (
                            <td key={col.key} className="sno">
                              {seg.isContinuation ? '' : row.lineNumber}
                            </td>
                          );
                        }
                        if (col.key === 'description') {
                          return (
                            <td key={col.key}>
                              <RowDescription row={row} segment={seg} />
                            </td>
                          );
                        }
                        const align = col.align === 'right' ? 'num' : undefined;
                        return (
                          <td key={col.key} className={align}>
                            {seg.showCommercial ? row.cells[col.key] ?? '' : ''}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

function MetaTables({
  left,
  right,
  sectionId,
}: {
  left: Array<{ label: string; value: string }>;
  right: Array<{ label: string; value: string }>;
  sectionId: string;
}) {
  return (
    <div className="print-generic-meta print-letter-party" {...sectionAttr(sectionId)}>
      <table>
        <tbody>
          {left.map((m, i) => (
            <tr key={`${m.label}-${i}`}>
              <th>{m.label}</th>
              <td>{m.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table>
        <tbody>
          {right.map((m, i) => (
            <tr key={`${m.label}-${i}`}>
              <th>{m.label}</th>
              <td>{m.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GenericPageBody({
  page,
  model,
}: {
  page: PlannedPage;
  model: PrintDocumentModel;
}) {
  const payload = (model.renderPayload || {}) as RichPrintRenderPayload;
  const metaLines = payload.metaLines || [];
  const mid = Math.ceil(metaLines.length / 2) || 1;

  return (
    <>
      {pageHas(page, 'documentHeader') ? (
        <>
          <h1 className="print-generic-title" {...sectionAttr('documentHeader')}>
            {model.title}
          </h1>
          {model.subtitle ? (
            <p className="print-generic-subtitle" {...sectionAttr('documentHeader')}>
              {model.subtitle}
            </p>
          ) : null}
        </>
      ) : null}

      {pageHas(page, 'partyBlock') && payload.partyBlock ? (
        <MetaTables
          left={payload.partyBlock.leftRows}
          right={payload.partyBlock.rightRows}
          sectionId="partyBlock"
        />
      ) : null}

      {pageHas(page, 'customerBlock') && !payload.partyBlock && metaLines.length > 0 ? (
        <MetaTables left={metaLines.slice(0, mid)} right={metaLines.slice(mid)} sectionId="customerBlock" />
      ) : null}

      {pageHas(page, 'intro') ? (
        <div className="print-generic-intro print-letter-intro" {...sectionAttr('intro')}>
          {payload.introGreeting ? <p>{payload.introGreeting}</p> : null}
          {(payload.introParagraphs || []).map((p) => (
            <p key={p.slice(0, 40)}>{p}</p>
          ))}
          {!payload.introGreeting && !payload.introParagraphs?.length && payload.introText ? (
            <p>{payload.introText}</p>
          ) : null}
        </div>
      ) : null}

      {pageHas(page, 'addressGrid') && payload.addressGrid?.length ? (
        <section className="print-letter-address-grid" {...sectionAttr('addressGrid')}>
          {payload.addressGrid.map((col) => (
            <div key={col.heading} className="print-letter-address-col">
              <h4>{col.heading}</h4>
              {col.name ? <p className="print-letter-party-name">{col.name}</p> : null}
              {col.lines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ))}
        </section>
      ) : null}

      {payload.sectionNodes
        ? Object.entries(payload.sectionNodes).map(([id, node]) =>
            pageHas(page, id) ? (
              <div key={id} className="print-generic-section-node" {...sectionAttr(id)}>
                {node}
              </div>
            ) : null
          )
        : null}

      {page.itemRows.length > 0 || pageHas(page, 'tableHeader') ? (
        <GenericTable model={model} page={page} />
      ) : null}

      {model.finalSections.map((s) => {
        if (!pageHas(page, s.id)) return null;
        if (s.id === 'terms') {
          return (
            <div key={s.id} className="print-letter-terms" {...sectionAttr(s.id)}>
              {payload.termsHeading ? <h4>{payload.termsHeading}</h4> : null}
              {payload.termRows?.length ? (
                <table>
                  <tbody>
                    {payload.termRows.map((r) => (
                      <tr key={r.label}>
                        <th>{r.label}</th>
                        <td>: {r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {(payload.termsExtra || []).map((t) => (
                <p key={t.slice(0, 48)} className="print-letter-terms-extra">
                  {t}
                </p>
              ))}
            </div>
          );
        }
        if (s.id === 'signers' && payload.signers?.length) {
          return (
            <div key={s.id} className="print-letter-signers" {...sectionAttr(s.id)}>
              <PrintableApprovalSigners signers={payload.signers} />
            </div>
          );
        }
        return (
          <div key={s.id} className="print-generic-final" {...sectionAttr(s.id)}>
            {payload.finalBlocks?.[s.id] ?? payload.sectionNodes?.[s.id] ?? null}
          </div>
        );
      })}
    </>
  );
}

export type GenericPaginatedDocumentProps = {
  model: PrintDocumentModel;
  plan?: PagePlan;
  onClose?: () => void;
  diagnostics?: boolean;
};

/** Generic composed-page renderer for any PrintDocumentModel. */
export const GenericPaginatedDocument: React.FC<GenericPaginatedDocumentProps> = ({
  model,
  plan: planProp,
  onClose,
  diagnostics,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = React.useState<{ model: PrintDocumentModel; measured: MeasuredBlocks } | null>(
    null
  );
  const measured = rendered?.model === model ? rendered.measured : undefined;

  const plan = React.useMemo(
    () => planProp ?? buildPagePlan(model, { diagnostics: true, measured }),
    [model, planProp, measured]
  );

  React.useLayoutEffect(() => {
    if (planProp || measured || !rootRef.current) return;
    const estimated = measurePrintBlocks(model, { template: resolvePrintTemplate(model.templateId) });
    setRendered({ model, measured: measureRenderedPrintBlocks(rootRef.current, model, estimated) });
  }, [model, planProp, measured]);

  return (
    <div ref={rootRef} style={{ display: 'contents' }}>
      <PaginatedPrintShell
        model={model}
        plan={plan}
        onClose={onClose}
        diagnostics={diagnostics}
        renderPage={({ page, model: m }) => (
          <GenericPageBody page={page} model={m} />
        )}
      />
    </div>
  );
};
